import { forwardRef, type ComponentPropsWithRef, type ComponentRef, type RefObject } from "react";

import { cva } from "class-variance-authority";

import * as RadixScrollArea from "@radix-ui/react-scroll-area";

import { cn } from "..";

interface ScrollAreaProps extends ComponentPropsWithRef<typeof RadixScrollArea.Root> {
  orientation?: "vertical" | "horizontal" | "both";
  viewportRef?: RefObject<HTMLDivElement | null>;
}

const ScrollArea = forwardRef<ComponentRef<typeof RadixScrollArea.Root>, ScrollAreaProps>(
  ({ className, type = "auto", orientation = "vertical", viewportRef, children, ...props }, forwardedRef) => (
    <RadixScrollArea.Root
      ref={forwardedRef}
      className={cn("relative overflow-hidden border-inherit", className)}
      type={type}
      {...props}
    >
      <RadixScrollArea.Viewport ref={viewportRef} className={"size-full border-inherit"}>
        {children}
      </RadixScrollArea.Viewport>
      {orientation === "vertical" && <ScrollBar orientation="vertical" />}
      {orientation === "horizontal" && <ScrollBar orientation="horizontal" />}
      {orientation === "both" && (
        <>
          <ScrollBar orientation="vertical" />
          <ScrollBar orientation="horizontal" />
        </>
      )}
      <RadixScrollArea.Corner />
    </RadixScrollArea.Root>
  ),
);

const scrollBarVariants = cva("ease-in-out flex touch-none transition-colors duration-150 select-none", {
  variants: {
    orientation: {
      vertical: "w-2.5 h-full border-l border-l-transparent p-px",
      horizontal: "",
    },
  },
  defaultVariants: {
    orientation: "vertical",
  },
});

const ScrollBar = forwardRef<
  ComponentRef<typeof RadixScrollArea.ScrollAreaScrollbar>,
  ComponentPropsWithRef<typeof RadixScrollArea.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, forwardedRef) => (
  <RadixScrollArea.ScrollAreaScrollbar
    ref={forwardedRef}
    className={scrollBarVariants({ orientation, className })}
    orientation={orientation}
    {...props}
  >
    <RadixScrollArea.ScrollAreaThumb className={"relative flex-[1_1_0%] rounded-full bg-primary-dark-gray"} />
  </RadixScrollArea.ScrollAreaScrollbar>
));

export { ScrollArea };
